/**
 * Provider config document types — shared between backend and admin-console.
 *
 * The `provider-config` document in CosmosDB (settings container) stores the
 * registry of available LLM providers. The backend loads this document at
 * startup to populate the in-memory `LLMProvider` registry.
 *
 * For the runtime registry implementation, see
 * `Backend/functions/src/prompt/llmProviders.ts` (backend-only).
 */

import type { LLMProviderFamily } from './llmProviderTypes';

/**
 * Lifecycle status of a provider — the ONLY usability field.
 *   - 'active'     — usable at runtime (default when omitted)
 *   - 'deprecated' — registered but not callable. fromKey() throws and
 *     keys() filters it out. Kept in the registry for audit trail and
 *     historical references.
 *
 * There was previously also a `ProviderConfig.enabled` boolean and a derived
 * `AppSettings.enabledProviders` allowlist. Neither gated anything: the registry
 * loaded every provider regardless and never even carried `enabled` into
 * `LLMProviderData`. Both are gone; `status` is what it always actually was.
 * See docs/FeatureDesigns/PipelineInfra/config-versioning.md §5.
 */
export type ProviderStatus = 'active' | 'deprecated';

/** Reasoning effort level — used by OpenAI reasoning models and as a provider default. */
export type ReasoningEffort = 'minimal' | 'low' | 'medium' | 'high';

/** Reasoning summary mode for OpenAI reasoning models.
 *  Maps to `reasoning.summary` on the Responses API.
 *    - 'none'     — no summary (saves output tokens)
 *    - 'auto'     — model decides summary length
 *    - 'concise'  — shorter summaries
 *    - 'detailed' — full reasoning trace */
export type ReasoningSummaryMode = 'none' | 'auto' | 'concise' | 'detailed';

export interface ProviderConfig {
    key: string;
    displayName: string;
    family: LLMProviderFamily;
    modelName: string;
    pricing: { input: number; output: number };
    /** Lifecycle status. Absent === 'active'. Deprecated providers cannot be used at runtime. */
    status?: ProviderStatus;
    /** Cross-provider failover (Step 5.5).
     *
     *  Key of another active provider. Used for transparent failover when this
     *  provider's call fails after intra-provider retries are exhausted. The
     *  router walks the chain up to a hard depth cap (2 hops total — original
     *  + backup + backup-of-backup) and emits a WARN log on each failover event.
     *
     *  Same-family backups are allowed (e.g. gpt-5 → gpt-4o for model-level
     *  failures); note they won't survive a provider-wide API outage — pick a
     *  different family if that's the failure mode you're hedging.
     *
     *  Validation rules (admin-side):
     *    - Must reference a known provider key
     *    - Backup must not be deprecated (status !== 'deprecated')
     *    - No self-reference (backupProvider !== this.key)
     *  Cycles (A→B→A) are NOT validated — the depth cap handles them.
     */
    backupProvider?: string;
    /** Optional name of the env var holding this provider's API key, overriding
     *  the family default. Lets two provider entries share a model but bill to
     *  different keys (e.g. a 'gemini-2.5-flash-paid' twin with
     *  apiKeyEnv='GEMINI_API_KEY_PAID' as the free-tier entry's backupProvider).
     *  Currently honored by the google family only. Falls back to the family
     *  default key when the env var is unset on the function app. */
    apiKeyEnv?: string;

    // ── Per-provider LLM defaults ────────────────────────────────────
    // Which fields are meaningful is determined by getProviderCapabilities().
    // Adapters fall back to their existing hardcoded defaults when absent.

    /** Default sampling temperature. Not supported by OpenAI reasoning models
     *  (GPT-5, o-series) or mock providers. */
    temperature?: number;
    /** Default max output tokens. */
    maxTokens?: number;
    /** Reasoning effort for OpenAI reasoning models (GPT-5, o-series).
     *  Maps to `reasoning.effort` on the Responses API. */
    reasoningEffort?: ReasoningEffort;
    /** Reasoning summary mode for OpenAI reasoning models.
     *  'none' = no summary, 'auto'/'concise'/'detailed' = include summary. */
    reasoningSummary?: ReasoningSummaryMode;
    /** Explicit thinking budget in tokens for Anthropic and Gemini.
     *  Anthropic minimum is 1024. Set to 0 to disable thinking. */
    thinkingBudgetTokens?: number;
    /** Advisory per-call cost cap in USD. Warning only; not enforced at call
     *  time. Surfaced in admin UI when an estimate exceeds the cap. */
    softCostCapUsd?: number;

    // ── Realtime / voice providers ──────────────────────────────────
    // Set on realtime audio providers only; ignored by text providers.

    /** Default voice id for realtime/voice providers (e.g. 'alloy'). */
    voice?: string;
    /** API endpoint URL for providers with non-standard endpoints
     *  (e.g. OpenAI Realtime: https://api.openai.com/v1/realtime/sessions). */
    url?: string;
}

// ── Slot params ──────────────────────────────────────────────────────────────

/**
 * Model-shaped LLM params, settable on a tier slot or a parallel branch.
 *
 * These are the params whose *applicability* depends on the model: which of them
 * are legal is decided by `getProviderCapabilities`, and their valid ranges
 * differ per family (Anthropic wants a thinking budget ≥1024 and below
 * `max_tokens`; Gemini treats 0 as off). A slot names one provider, so it is the
 * narrowest place that knows both the family and the operation.
 *
 * Deliberately excludes `maxTokens`, `temperature` and `timeoutMs` — those are
 * task-shaped, hold across a whole ladder regardless of which rung serves it,
 * and belong on the runstep.
 *
 * See docs/FeatureDesigns/PipelineInfra/provider-and-parameter-management.md §3.
 */
export interface SlotParams {
    thinkingBudgetTokens?: number;
    reasoningEffort?: ReasoningEffort;
    reasoningSummary?: ReasoningSummaryMode;
    useStrictJsonSchema?: boolean;
}

/** The `SlotParams` keys, for validation and normalization walks. */
export const SLOT_PARAM_KEYS = [
    'thinkingBudgetTokens',
    'reasoningEffort',
    'reasoningSummary',
    'useStrictJsonSchema',
] as const satisfies readonly (keyof SlotParams)[];

/**
 * Parse a persisted `params` blob, keeping only recognized keys with the right
 * primitive type. Returns undefined when nothing survives, so an empty object
 * never persists and `params === undefined` keeps its "inherit" meaning.
 */
export function normalizeSlotParams(raw: unknown): SlotParams | undefined {
    if (!raw || typeof raw !== 'object') return undefined;
    const o = raw as Record<string, unknown>;
    const out: SlotParams = {};
    if (typeof o.thinkingBudgetTokens === 'number' && Number.isFinite(o.thinkingBudgetTokens)) {
        out.thinkingBudgetTokens = o.thinkingBudgetTokens;
    }
    if (o.reasoningEffort === 'minimal' || o.reasoningEffort === 'low'
        || o.reasoningEffort === 'medium' || o.reasoningEffort === 'high') {
        out.reasoningEffort = o.reasoningEffort;
    }
    if (o.reasoningSummary === 'none' || o.reasoningSummary === 'auto'
        || o.reasoningSummary === 'concise' || o.reasoningSummary === 'detailed') {
        out.reasoningSummary = o.reasoningSummary;
    }
    if (typeof o.useStrictJsonSchema === 'boolean') {
        out.useStrictJsonSchema = o.useStrictJsonSchema;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Validate `params` against what the provider's model can actually do.
 * Returns human-readable errors; empty means valid.
 *
 * Two rules:
 *   1. Every set param must be supported by the model — otherwise it is a
 *      control that silently does nothing at call time, which is the failure
 *      this whole layer exists to remove.
 *   2. `reasoningEffort` and `thinkingBudgetTokens` may not both be set. They
 *      are not two spellings of one control: the effort→budget mapping is
 *      per-adapter and inconsistent ('medium' means "family default" on Gemini
 *      but "thinking off" on Claude; 'high' is 8192 vs 8000). Setting both is
 *      contradictory intent, not redundancy.
 */
export function validateSlotParams(
    params: SlotParams | undefined,
    family: LLMProviderFamily,
    modelName: string,
    where: string,
): string[] {
    if (!params) return [];
    const caps = getProviderCapabilities(family, modelName);
    const errors: string[] = [];
    for (const key of SLOT_PARAM_KEYS) {
        if (params[key] === undefined) continue;
        if (!caps[key]) {
            errors.push(`${where}: '${key}' is not supported by ${family}/${modelName}`);
        }
    }
    if (params.reasoningEffort !== undefined && params.thinkingBudgetTokens !== undefined) {
        errors.push(
            `${where}: set either 'reasoningEffort' or 'thinkingBudgetTokens', not both — ` +
            `they resolve to the same thinking budget by different, per-adapter mappings`,
        );
    }
    return errors;
}

// ── Capability matrix ────────────────────────────────────────────────────────

export interface ProviderCapabilities {
    temperature: boolean;
    maxTokens: boolean;
    reasoningEffort: boolean;
    reasoningSummary: boolean;
    useStrictJsonSchema: boolean;
    thinkingBudgetTokens: boolean;
}

/** OpenAI reasoning model detection — same regex used in the adapters. */
export function isOpenAIReasoningModel(modelName: string): boolean {
    return /^(gpt-5|o\d)/i.test(modelName);
}

/**
 * Deterministic capability matrix from (family, modelName).
 * Used by the admin UI to show only relevant fields, and by the backend
 * to know which provider defaults to read.
 */
export function getProviderCapabilities(family: LLMProviderFamily, modelName: string): ProviderCapabilities {
    if (family === 'mock') {
        return {
            temperature: false,
            maxTokens: false,
            reasoningEffort: false,
            reasoningSummary: false,
            useStrictJsonSchema: false,
            thinkingBudgetTokens: false,
        };
    }
    if (family === 'openai') {
        const reasoning = isOpenAIReasoningModel(modelName);
        return {
            temperature: !reasoning,
            maxTokens: true,
            reasoningEffort: reasoning,
            reasoningSummary: reasoning,
            useStrictJsonSchema: true,
            thinkingBudgetTokens: false,
        };
    }
    if (family === 'anthropic') {
        return {
            temperature: true,
            maxTokens: true,
            // Not a native Anthropic knob, but claude.ts maps it to an extended
            // thinking budget (`thinkingBudgetForEffort`), so it IS meaningful
            // here. `thinkingBudgetTokens` still wins when both are set.
            reasoningEffort: true,
            // Output-only on this family: the adapter extracts emitted thinking
            // blocks INTO `reasoningSummary`, but never reads it as a setting.
            // Only openaiResponses.ts consumes `params.reasoningSummary`.
            reasoningSummary: false,
            useStrictJsonSchema: false,
            thinkingBudgetTokens: true,
        };
    }
    if (family === 'xai') {
        // xAI Grok speaks the OpenAI chat-completions dialect (incl. strict
        // json_schema structured output). These are SAFE defaults — per-model
        // nuances (e.g. whether grok-4 accepts `temperature`/`reasoning_effort`)
        // MUST be verified against the live xAI API before prod use. See
        // docs/FeatureDesigns/PipelineInfra/grok-xai-provider.md §Open items.
        const reasoning = /grok-4|grok-3-mini|reasoning/i.test(modelName);
        return {
            temperature: true,
            maxTokens: true,
            reasoningEffort: reasoning,
            reasoningSummary: false,
            useStrictJsonSchema: true,
            thinkingBudgetTokens: false,
        };
    }
    if (family === 'deepseek') {
        // deepseek.ts: thinking is switched on by `reasoningEffort` (off when
        // unset); `temperature` applies only with thinking off; no strict
        // json_schema mode (json_object + schema in the system prompt), and no
        // token budget — DeepSeek's thinking control is effort only.
        return {
            temperature: true,
            maxTokens: true,
            reasoningEffort: true,
            reasoningSummary: false,
            useStrictJsonSchema: false,
            thinkingBudgetTokens: false,
        };
    }
    // google
    return {
        temperature: true,
        maxTokens: true,
        // Same as anthropic: gemini.ts resolves the thinking budget as
        // `thinkingBudgetTokens ?? thinkingBudgetForEffort(reasoningEffort) ??
        // family default`, and additionally turns on `includeThoughts` at
        // effort 'high'. Declaring it false made the provider editor strip a
        // field the adapter honours.
        reasoningEffort: true,
        // Output-only here too — see the anthropic branch.
        reasoningSummary: false,
        useStrictJsonSchema: false,
        thinkingBudgetTokens: true,
    };
}

export interface ProviderConfigDoc {
    id: string;  // 'provider-config'
    providers: ProviderConfig[];
    updatedAt: string;
    updatedBy?: string;
}
